import { DOCUMENT } from '@angular/common'
import { HttpErrorResponse, HttpEvent, HttpEventType, HttpHandlerFn, HttpRequest } from '@angular/common/http'
import { inject } from '@angular/core'
import { Observable, throwError } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { MessageType } from '../../../services/message/message-type'
import { MessageService } from '../../../services/message/message.service'
import { Messages } from '../../../services/workflow/models/messages'

export const DEFAULT_ERROR_MESSAGES = {
  en: 'This service is temporarily unavailable.',
  fr: 'Le service n’est pas disponible en ce moment.'
}

/**
 * Intercept any JSON responses and display any messages that are found in
 * the body
 *
 * @param req   Current HTTP Request that is being made
 * @param next  HTTP Handler that will take over when the request is complete
 *
 * @returns     Returns an observable of the event stream
 */
export function messageInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  let messageService = inject(MessageService)
  let lang = inject(DOCUMENT).documentElement.lang

  if (req.responseType === 'json') {
    let interceptedRequest = req.clone()
    return next(interceptedRequest)
      .pipe(
        map(it => handleHttpResponse(messageService, it)),
        catchError(it => handleHttpErrorResponse(lang, messageService, it as HttpErrorResponse))
      )
  }

  return next(req)
}

/**
 * Handles incoming HTTP response and will scan the body for messages
 *
 * @param messageService  The injected service that acts as the message storage
 * @param event           The HTTP response that was returned
 *
 * @returns               Returns the incoming event parameter
 */
function handleHttpResponse(messageService: MessageService, event: HttpEvent<unknown>): HttpEvent<unknown> {
  if (event.type === HttpEventType.Response && event.body) {
    if (containsErrorMessages(event.body)) {
      throw new HttpErrorResponse({ error: event.body })
    }

    if (containsMessages(event.body)) {
      displayMessages(messageService, event.body.messages)
      return event
    }
  }

  return event
}

/**
 * Handles incoming HTTP error response and will display any messages
 * found in the body
 *
 * @param lang            The current language of the page
 * @param messageService  The injected service that acts as the message storage
 * @param error           The HTTP response that was returned
 *
 * @returns               Returns an observable that never emits
 */
function handleHttpErrorResponse(lang: string, messageService: MessageService, error: HttpErrorResponse): Observable<never> {
  if (error instanceof HttpErrorResponse) {
    if (error.error && containsErrorMessages(error.error)) {
      displayMessages(messageService, error.error.messages)
    }

    else {
      displayMessages(messageService, {
        error: [
          DEFAULT_ERROR_MESSAGES[lang]
        ],
        warning: [],
        info: [],
        success: [],
      })
    }
  }

  return throwError(error)
}

/**
 * Checks if the body contains any messages
 *
 * @param body  Incoming object to test
 *
 * @returns     Returns `TRUE` if the object matches the expected format
 */
function containsMessages(body: unknown): body is { messages: Messages } {
  return typeof body === 'object'
    && 'messages' in body
    && body.messages
    && typeof body.messages === 'object'
    && 'error' in body.messages
    && 'warning' in body.messages
    && 'info' in body.messages
    && 'success' in body.messages
}

/**
 * Checks if the body contains any error messages
 *
 * @param response  Incoming object to test
 *
 * @returns          Returns `TRUE` if the object contains one or more error messages
 */
function containsErrorMessages(response: unknown): response is { messages: Messages } {
  return containsMessages(response)
    && response.messages.error.length > 0
}

/**
 * Display the messages extracted from the HTTP response
 *
 * @param messageService  The injected service that acts as the message storage
 * @param messages        A collection of messages to be displayed
 */
function displayMessages(messageService: MessageService, messages: Messages): void {
  for (let level of Object.keys(messages)) {
    for (let text of messages[level]) {
      if (level === 'success') {
        messageService.add({
          level,
          text,
          type: MessageType.TOAST
        })
      }

      else {
        messageService.add({
          level,
          text,
          type: MessageType.ALERT
        })
      }
    }
  }
}
